import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCurrency } from '../contexts/CurrencyContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import Header from './Header';
import Footer from './Footer';
import FreeShippingBadge from './FreeShippingBadge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight, Package, Shield, FlaskConical, CheckCircle, X, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CartPage = () => {
  const { t, currentLanguage } = useLanguage();
  const { cartItems, updateQuantity, removeFromCart, clearCart, loading, getCartTotal } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoError, setPromoError] = useState('');

  const getProductName = (product) => {
    if (!product?.name) return 'Unknown Product';
    return product.name[currentLanguage] || product.name.en || product.name;
  };

  const formatPrice = useCurrency().formatPrice;

  const content = {
    en: {
      title: "Shopping Cart",
      empty: "Your cart is empty",
      emptySubtext: "Add some products to get started",
      continueShopping: "Continue Shopping",
      product: "Product",
      price: "Price",
      quantity: "Quantity",
      total: "Total",
      subtotal: "Subtotal",
      shipping: "Shipping",
      shippingNote: "Calculated at checkout",
      orderTotal: "Order Total",
      checkout: "Send Order for Review",
      clearCart: "Clear Cart",
      remove: "Remove",
      disclaimerTitle: "Licensed Professionals Only",
      disclaimerText: "To ensure compliance and quality care, we exclusively supply licensed medical professionals (laboratories, clinics, and authorized institutions). Orders are processed after KYC verification (24-48 hours).",
      disclaimerNote: "By proceeding to checkout, you confirm you represent an authorized entity with legal permission to purchase research compounds.",
      promoCode: "Promo Code",
      promoPlaceholder: "Enter code",
      apply: "Apply",
      discount: "Discount",
      promoApplied: "Promo code applied!",
      removePromo: "Remove",
      promoExpired: "Promo code has expired",
      promoIncorrect: "Promo code is incorrect"
    },
    es: {
      title: "Carrito de Compras",
      empty: "Tu carrito está vacío",
      emptySubtext: "Añade algunos productos para empezar",
      continueShopping: "Continuar Comprando",
      product: "Producto",
      price: "Precio",
      quantity: "Cantidad",
      total: "Total",
      subtotal: "Subtotal",
      shipping: "Envío",
      shippingNote: "Calculado al finalizar",
      orderTotal: "Total del Pedido",
      checkout: "Enviar Pedido para Revisión",
      clearCart: "Vaciar Carrito",
      remove: "Eliminar",
      disclaimerTitle: "Solo Profesionales Autorizados",
      disclaimerText: "Para garantizar el cumplimiento y la atención de calidad, suministramos exclusivamente a profesionales médicos autorizados (laboratorios, clínicas e instituciones autorizadas). Los pedidos se procesan después de la verificación KYC (24-48 horas).",
      disclaimerNote: "Al proceder al pago, confirma que representa a una entidad autorizada con permiso legal para comprar compuestos de investigación.",
      promoCode: "Código Promocional",
      promoPlaceholder: "Ingrese código",
      apply: "Aplicar",
      discount: "Descuento",
      promoApplied: "¡Código aplicado!",
      removePromo: "Eliminar",
      promoExpired: "El código promocional ha expirado",
      promoIncorrect: "El código promocional es incorrecto"
    },
    ru: {
      title: "Корзина",
      empty: "Ваша корзина пуста",
      emptySubtext: "Добавьте товары, чтобы начать",
      continueShopping: "Продолжить покупки",
      product: "Товар",
      price: "Цена",
      quantity: "Количество",
      total: "Итого",
      subtotal: "Промежуточный итог",
      shipping: "Доставка",
      shippingNote: "Рассчитывается при оформлении",
      orderTotal: "Сумма заказа",
      checkout: "Отправить заказ на рассмотрение",
      clearCart: "Очистить корзину",
      remove: "Удалить",
      disclaimerTitle: "Только для лицензированных специалистов",
      disclaimerText: "Для обеспечения соответствия требованиям и качественного обслуживания мы осуществляем поставки исключительно лицензированным медицинским специалистам (лаборатории, клиники и аккредитованные учреждения). Заказы обрабатываются после прохождения KYC-верификации (24-48 часов).",
      disclaimerNote: "Оформляя заказ, вы подтверждаете, что представляете аккредитованное учреждение с законным правом на приобретение исследовательских соединений.",
      promoCode: "Промокод",
      promoPlaceholder: "Введите код",
      apply: "Применить",
      discount: "Скидка",
      promoApplied: "Промокод применён!",
      removePromo: "Удалить",
      promoExpired: "Срок действия промокода истёк",
      promoIncorrect: "Неверный промокод"
    },
    de: {
      title: "Warenkorb", empty: "Ihr Warenkorb ist leer", emptySubtext: "Fügen Sie Produkte hinzu, um zu beginnen",
      continueShopping: "Weiter einkaufen", product: "Produkt", price: "Preis", quantity: "Menge", total: "Summe",
      subtotal: "Zwischensumme", shipping: "Versand", shippingNote: "Berechnet beim Checkout", orderTotal: "Bestellsumme",
      checkout: "Bestellung zur Prüfung senden", clearCart: "Warenkorb leeren", remove: "Entfernen",
      disclaimerTitle: "Nur lizenzierte Fachkräfte",
      disclaimerText: "Zur Gewährleistung von Compliance und Qualität beliefern wir ausschließlich lizenzierte medizinische Fachkräfte (Labore, Kliniken und autorisierte Institutionen). Bestellungen werden nach KYC-Verifizierung (24-48 Stunden) bearbeitet.",
      disclaimerNote: "Mit dem Checkout bestätigen Sie, eine autorisierte Einrichtung mit rechtlicher Erlaubnis zum Erwerb von Forschungsverbindungen zu vertreten.",
      promoCode: "Promo-Code", promoPlaceholder: "Code eingeben", apply: "Anwenden", discount: "Rabatt",
      promoApplied: "Promo-Code angewendet!", removePromo: "Entfernen",
      promoExpired: "Promo-Code ist abgelaufen", promoIncorrect: "Promo-Code ist ungültig"
    },
    fr: {
      title: "Panier", empty: "Votre panier est vide", emptySubtext: "Ajoutez des produits pour commencer",
      continueShopping: "Continuer mes achats", product: "Produit", price: "Prix", quantity: "Quantité", total: "Total",
      subtotal: "Sous-total", shipping: "Livraison", shippingNote: "Calculé au paiement", orderTotal: "Total de la commande",
      checkout: "Envoyer la commande pour révision", clearCart: "Vider le panier", remove: "Supprimer",
      disclaimerTitle: "Réservé aux professionnels agréés",
      disclaimerText: "Pour garantir la conformité et la qualité des soins, nous fournissons exclusivement des professionnels médicaux agréés (laboratoires, cliniques et institutions autorisées). Les commandes sont traitées après vérification KYC (24-48 heures).",
      disclaimerNote: "En passant à la caisse, vous confirmez représenter une entité agréée disposant de l'autorisation légale d'acheter des composés de recherche.",
      promoCode: "Code promo", promoPlaceholder: "Saisir le code", apply: "Appliquer", discount: "Remise",
      promoApplied: "Code promo appliqué !", removePromo: "Supprimer",
      promoExpired: "Le code promo a expiré", promoIncorrect: "Code promo incorrect"
    },
    it: {
      title: "Carrello", empty: "Il tuo carrello è vuoto", emptySubtext: "Aggiungi prodotti per iniziare",
      continueShopping: "Continua gli acquisti", product: "Prodotto", price: "Prezzo", quantity: "Quantità", total: "Totale",
      subtotal: "Subtotale", shipping: "Spedizione", shippingNote: "Calcolato al checkout", orderTotal: "Totale ordine",
      checkout: "Invia ordine per revisione", clearCart: "Svuota carrello", remove: "Rimuovi",
      disclaimerTitle: "Solo professionisti autorizzati",
      disclaimerText: "Per garantire conformità e qualità, riforniamo esclusivamente professionisti sanitari autorizzati (laboratori, cliniche e istituzioni autorizzate). Gli ordini vengono elaborati dopo la verifica KYC (24-48 ore).",
      disclaimerNote: "Procedendo al pagamento, confermi di rappresentare un'entità autorizzata con permesso legale per acquistare composti da ricerca.",
      promoCode: "Codice promo", promoPlaceholder: "Inserisci codice", apply: "Applica", discount: "Sconto",
      promoApplied: "Codice promo applicato!", removePromo: "Rimuovi",
      promoExpired: "Il codice promo è scaduto", promoIncorrect: "Codice promo non valido"
    },
    pl: {
      title: "Koszyk", empty: "Twój koszyk jest pusty", emptySubtext: "Dodaj produkty, aby rozpocząć",
      continueShopping: "Kontynuuj zakupy", product: "Produkt", price: "Cena", quantity: "Ilość", total: "Razem",
      subtotal: "Suma częściowa", shipping: "Wysyłka", shippingNote: "Obliczone przy kasie", orderTotal: "Razem do zapłaty",
      checkout: "Wyślij zamówienie do weryfikacji", clearCart: "Wyczyść koszyk", remove: "Usuń",
      disclaimerTitle: "Tylko dla licencjonowanych specjalistów",
      disclaimerText: "Aby zapewnić zgodność i wysoką jakość obsługi, zaopatrujemy wyłącznie licencjonowanych specjalistów medycznych (laboratoria, kliniki i autoryzowane instytucje). Zamówienia są realizowane po weryfikacji KYC (24-48 godzin).",
      disclaimerNote: "Przechodząc do kasy potwierdzasz, że reprezentujesz autoryzowany podmiot z prawem do zakupu związków badawczych.",
      promoCode: "Kod promocyjny", promoPlaceholder: "Wpisz kod", apply: "Zastosuj", discount: "Rabat",
      promoApplied: "Kod promocyjny zastosowany!", removePromo: "Usuń",
      promoExpired: "Kod promocyjny wygasł", promoIncorrect: "Nieprawidłowy kod promocyjny"
    },
    nl: {
      title: "Winkelwagen", empty: "Uw winkelwagen is leeg", emptySubtext: "Voeg producten toe om te beginnen",
      continueShopping: "Verder winkelen", product: "Product", price: "Prijs", quantity: "Aantal", total: "Totaal",
      subtotal: "Subtotaal", shipping: "Verzending", shippingNote: "Berekend bij afrekenen", orderTotal: "Besteltotaal",
      checkout: "Bestelling ter beoordeling versturen", clearCart: "Winkelwagen legen", remove: "Verwijderen",
      disclaimerTitle: "Alleen gelicentieerde professionals",
      disclaimerText: "Om naleving en kwaliteit te garanderen, leveren wij uitsluitend aan gelicentieerde medische professionals (laboratoria, klinieken en geautoriseerde instellingen). Bestellingen worden verwerkt na KYC-verificatie (24-48 uur).",
      disclaimerNote: "Door af te rekenen bevestigt u dat u een geautoriseerde entiteit vertegenwoordigt met wettelijke toestemming om onderzoeksverbindingen te kopen.",
      promoCode: "Promocode", promoPlaceholder: "Code invoeren", apply: "Toepassen", discount: "Korting",
      promoApplied: "Promocode toegepast!", removePromo: "Verwijderen",
      promoExpired: "Promocode is verlopen", promoIncorrect: "Promocode is onjuist"
    },
    pt: {
      title: "Carrinho", empty: "Seu carrinho está vazio", emptySubtext: "Adicione produtos para começar",
      continueShopping: "Continuar comprando", product: "Produto", price: "Preço", quantity: "Quantidade", total: "Total",
      subtotal: "Subtotal", shipping: "Envio", shippingNote: "Calculado no checkout", orderTotal: "Total do pedido",
      checkout: "Enviar pedido para revisão", clearCart: "Limpar carrinho", remove: "Remover",
      disclaimerTitle: "Apenas profissionais licenciados",
      disclaimerText: "Para garantir conformidade e qualidade, fornecemos exclusivamente a profissionais médicos licenciados (laboratórios, clínicas e instituições autorizadas). Pedidos são processados após verificação KYC (24-48 horas).",
      disclaimerNote: "Ao prosseguir para o checkout, você confirma representar uma entidade autorizada com permissão legal para comprar compostos de pesquisa.",
      promoCode: "Código promocional", promoPlaceholder: "Digite o código", apply: "Aplicar", discount: "Desconto",
      promoApplied: "Código promocional aplicado!", removePromo: "Remover",
      promoExpired: "O código promocional expirou", promoIncorrect: "Código promocional inválido"
    }
  };

  const c = content[currentLanguage] || content.en;
  const cartTotal = getCartTotal();

  // Apply promo code
  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    
    setPromoLoading(true);
    setPromoError('');
    
    try {
      const response = await fetch(`${API_URL}/api/promo-codes/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: promoCode.trim(),
          cart_total: cartTotal
        })
      });
      
      const data = await response.json();
      
      if (data.valid) {
        setAppliedPromo(data);
        setPromoError('');
        // Store in session for checkout
        sessionStorage.setItem('appliedPromoCode', promoCode.trim().toUpperCase());
        sessionStorage.setItem('promoDiscount', JSON.stringify(data));
      } else {
        // Set specific error message based on error type
        if (data.error === 'expired') {
          setPromoError(c.promoExpired);
        } else if (data.error === 'incorrect') {
          setPromoError(c.promoIncorrect);
        } else {
          setPromoError(data.message || c.promoIncorrect);
        }
        setAppliedPromo(null);
      }
    } catch (err) {
      setPromoError(c.promoIncorrect);
    } finally {
      setPromoLoading(false);
    }
  };

  // Remove promo code
  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoCode('');
    setPromoError('');
    sessionStorage.removeItem('appliedPromoCode');
    sessionStorage.removeItem('promoDiscount');
  };

  // Calculate totals with discount
  const subtotal = cartTotal;
  const discountAmount = appliedPromo ? appliedPromo.discount_amount : 0;
  const finalTotal = subtotal - discountAmount;

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-capsule-bg">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="text-center py-20">
            <div className="w-24 h-24 mx-auto mb-6 rounded-3xl bg-gradient-to-br from-capsule-teal/10 to-capsule-aqua/10 flex items-center justify-center">
              <ShoppingBag className="w-12 h-12 text-capsule-teal" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{c.empty}</h1>
            <p className="text-gray-500 mb-8">{c.emptySubtext}</p>
            <Link to="/products">
              <Button className="bg-gradient-to-r from-capsule-teal to-capsule-aqua hover:from-capsule-teal-dark hover:to-capsule-teal text-white px-8 py-3 rounded-full font-semibold shadow-teal-glow">
                {c.continueShopping}
              </Button>
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-capsule-bg">
      <Header />

      {/* Hero Banner - Capsule 8 Design */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-capsule-teal-dark">
        <div className="absolute right-0 top-0 bottom-0 w-96 opacity-20">
          <svg viewBox="0 0 200 400" fill="none" className="w-full h-full">
            <defs>
              <linearGradient id="cartGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#14B8A6" />
                <stop offset="100%" stopColor="#22D3EE" />
              </linearGradient>
            </defs>
            <path d="M50 0 Q120 50 50 100 Q-20 150 50 200 Q120 250 50 300 Q-20 350 50 400" 
                  stroke="url(#cartGradient)" strokeWidth="3" fill="none"/>
            <path d="M150 0 Q80 50 150 100 Q220 150 150 200 Q80 250 150 300 Q220 350 150 400" 
                  stroke="url(#cartGradient)" strokeWidth="3" fill="none"/>
          </svg>
        </div>
        
        <div className="absolute top-10 right-32 w-20 h-20 rounded-full bg-gradient-to-br from-capsule-rose-gold/30 to-capsule-salmon/20 blur-xl"></div>
        <div className="absolute bottom-5 right-48 w-16 h-16 rounded-full bg-gradient-to-br from-capsule-teal/30 to-capsule-aqua/20 blur-xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-10 md:py-12">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full mb-4 border border-white/20">
              <ShoppingBag className="w-4 h-4 text-capsule-aqua" />
              <span className="text-sm font-medium text-capsule-aqua">{cartItems.length} items</span>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">{c.title}</h1>
            <p className="text-gray-300">{t('cart_review_subtitle')}</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 md:py-12 -mt-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item) => (
              <div 
                key={`${item.product_id}-${item.selected_variant}`}
                className="bg-white/80 backdrop-blur-lg rounded-2xl p-4 md:p-6 shadow-lg border border-white/50 hover:shadow-xl transition-all"
              >
                <div className="flex gap-4">
                  {/* Product Image - Clickable */}
                  <Link 
                    to={`/product/${item.product_id}`}
                    className="w-24 h-24 md:w-32 md:h-32 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl flex-shrink-0 overflow-hidden hover:ring-2 hover:ring-capsule-teal transition-all"
                  >
                    <img 
                      src={item.product?.image_url || '/placeholder.png'} 
                      alt={getProductName(item.product)}
                      className="w-full h-full object-contain p-2"
                    />
                  </Link>

                  {/* Product Info */}
                  <div className="flex-1 min-w-0">
                    {/* Product Name - Clickable */}
                    <Link 
                      to={`/product/${item.product_id}`}
                      className="hover:text-capsule-teal transition-colors"
                    >
                      <h3 className="font-semibold text-gray-900 text-sm md:text-base line-clamp-2">
                        {getProductName(item.product)}
                      </h3>
                    </Link>
                    {/* Dosage - show selected variant OR product dosage OR first variant dosage */}
                    {(item.selected_variant || item.product?.dosage || (item.product?.variants && item.product?.variants[0]?.dosage)) && (
                      <p className="text-xs md:text-sm text-capsule-teal mt-1 font-medium bg-capsule-teal/10 inline-block px-2 py-0.5 rounded-full">
                        <FlaskConical className="w-3 h-3 inline mr-1" />
                        {item.selected_variant || item.product?.dosage || item.product?.variants?.[0]?.dosage}
                      </p>
                    )}
                    
                    <div className="flex items-center gap-3 mt-3">
                      {/* Quantity Controls */}
                      <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1">
                        <button 
                          onClick={() => updateQuantity(item.id, Math.max(1, item.quantity - 1))}
                          className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-white transition-colors"
                          disabled={loading}
                        >
                          <Minus className="w-4 h-4 text-gray-600" />
                        </button>
                        <span className="w-8 text-center font-medium text-gray-900">{item.quantity}</span>
                        <button 
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-white transition-colors"
                          disabled={loading}
                        >
                          <Plus className="w-4 h-4 text-gray-600" />
                        </button>
                      </div>

                      {/* Remove Button */}
                      <button 
                        onClick={() => removeFromCart(item.id)}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                        disabled={loading}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Price - Use item.price or fallback to product price */}
                  <div className="text-right">
                    <p className="text-lg font-bold text-capsule-teal">
                      {formatPrice((item.price || item.product?.price || 0) * item.quantity)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatPrice(item.price || item.product?.price || 0)} each
                    </p>
                  </div>
                </div>
              </div>
            ))}

            {/* Clear Cart Button */}
            <div className="flex justify-end pt-2">
              <Button 
                variant="outline" 
                onClick={clearCart}
                className="text-gray-600 hover:text-red-500 hover:border-red-200 rounded-full"
                disabled={loading}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {c.clearCart}
              </Button>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white/90 backdrop-blur-lg rounded-2xl p-6 shadow-xl border border-white/50 sticky top-24">
              <h2 className="text-xl font-bold text-gray-900 mb-4">{c.orderTotal}</h2>

              {/* Free-shipping live tracker, reacts to subtotal */}
              <div className="mb-4" data-testid="cart-free-shipping-tracker">
                <FreeShippingBadge variant="banner" subtotalEur={subtotal} />
              </div>

              {/* Totals */}
              <div className="space-y-3">
                <div className="flex justify-between text-gray-600">
                  <span>{c.subtotal}</span>
                  <span>{formatPrice(subtotal)}</span>
                </div>
                
                {appliedPromo && (
                  <div className="flex justify-between text-green-600">
                    <span>{c.discount} ({appliedPromo.discount_percent}%)</span>
                    <span>-{formatPrice(discountAmount)}</span>
                  </div>
                )}
                
                <div className="flex justify-between text-gray-600">
                  <span>{c.shipping}</span>
                  <span className="text-sm text-gray-400">{c.shippingNote}</span>
                </div>
                
                <div className="border-t border-gray-200 pt-3 mt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-gray-900">{c.total}</span>
                    <span className="text-2xl font-bold bg-gradient-to-r from-capsule-teal to-capsule-aqua bg-clip-text text-transparent">
                      {formatPrice(finalTotal)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Promo Code Section - Compact below total */}
              <div className="mt-4 pt-4 border-t border-gray-100">
                {appliedPromo ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-2.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className="font-mono text-sm font-bold text-green-700">{appliedPromo.code}</span>
                        <span className="text-xs text-green-600">(-{appliedPromo.discount_percent}%)</span>
                      </div>
                      <button 
                        onClick={handleRemovePromo}
                        className="text-gray-400 hover:text-red-500 p-0.5"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      placeholder={c.promoPlaceholder}
                      className="rounded-lg font-mono uppercase text-sm h-9"
                      onKeyPress={(e) => e.key === 'Enter' && handleApplyPromo()}
                      data-testid="promo-code-input"
                    />
                    <Button
                      onClick={handleApplyPromo}
                      disabled={promoLoading || !promoCode.trim()}
                      className="bg-capsule-teal hover:bg-capsule-teal-dark text-white rounded-lg px-3 h-9 text-sm"
                      data-testid="apply-promo-btn"
                    >
                      {promoLoading ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        c.apply
                      )}
                    </Button>
                  </div>
                )}
                
                {promoError && (
                  <p className="text-xs text-red-500 mt-1.5">{promoError}</p>
                )}
              </div>

              {/* Send Order for Review Button */}
              <Button 
                className="w-full mt-6 bg-gradient-to-r from-capsule-teal to-capsule-aqua hover:from-capsule-teal-dark hover:to-capsule-teal text-white py-6 rounded-xl text-lg font-semibold shadow-lg hover:shadow-teal-glow transition-all"
                onClick={() => navigate('/checkout')}
                data-testid="checkout-btn"
              >
                <Package className="w-5 h-5 mr-2" />
                {c.checkout}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>

              {/* Security Badges */}
              <div className="flex items-center justify-center gap-4 mt-4 text-gray-400">
                <Shield className="w-5 h-5" />
                <span className="text-xs">{currentLanguage === 'es' ? 'Proceso Seguro' : currentLanguage === 'ru' ? 'Безопасный Процесс' : 'Secure Process'}</span>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="mt-6 bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-5 border border-orange-200">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <FlaskConical className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-orange-800 text-sm">{c.disclaimerTitle}</h4>
                  <p className="text-xs text-orange-700 mt-1 leading-relaxed">{c.disclaimerText}</p>
                  <p className="text-xs text-orange-600 mt-2 italic">{c.disclaimerNote}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CartPage;
